/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import { token } from '@atlaskit/tokens';

import type { SuggestionProfile } from '../../aiSuggestionsPluginType';
import { SuggestionAvatar } from './SuggestionAvatar';

const MAX_VISIBLE_AVATARS = 3;
const AVATAR_SIZE = 28;
const EXPANDED_AVATAR_SLOT_WIDTH = 16;
const MAX_PREVIEW_WIDTH = 180;
const EXPANDED_EDGE_OFFSET = 2;
// Preview text margin (2px), preview padding (6px), and button padding (2px + 8px).
const EXPANDED_INLINE_SPACING = 2 + 6 + 2 + 8;
// 24px gutter + 48px object actions width + 24px gutter.
const RIGHT_EDGE_CLEARANCE = 24 + 48 + 24;

// Compiled allows the custom transition values used by the indicator.
const styles = cssMap({
	indicatorBase: {
		position: 'relative',
		display: 'flex',
		height: AVATAR_SIZE,
		width: AVATAR_SIZE,
	},
	button: {
		position: 'absolute',
		// above remix icon.
		zIndex: 2,
		top: 0,
		left: 0,
		// Start with a numeric right anchor so leftward expansion can interpolate.
		right: 0,
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		height: AVATAR_SIZE,
		width: 'max-content',
		minWidth: AVATAR_SIZE,
		border: 'none',
		borderTopLeftRadius: token('radius.xxlarge'),
		borderTopRightRadius: token('radius.xxlarge'),
		borderBottomRightRadius: token('radius.xxlarge'),
		borderBottomLeftRadius: token('radius.small'),
		backgroundColor: token('elevation.rovo.surface.overlay'),
		boxShadow: token('elevation.shadow.overflow'),
		cursor: 'pointer',
		overflow: 'hidden',
		paddingInline: 0,
		// Separate the hover properties so Compiled preserves their transition fallbacks.
		transition: `
			background-color ${token('motion.duration.short')} ${token('motion.easing.out.practical')},
			border-color ${token('motion.duration.short')} ${token('motion.easing.out.practical')},
			height ${token('motion.duration.medium')} ${token('motion.easing.in.practical')} ${token('motion.duration.xxshort')},
			top ${token('motion.duration.medium')} ${token('motion.easing.in.practical')} ${token('motion.duration.xxshort')},
			left ${token('motion.duration.medium')} ${token('motion.easing.in.practical')} ${token('motion.duration.xxshort')},
			right ${token('motion.duration.medium')} ${token('motion.easing.in.practical')} ${token('motion.duration.xxshort')},
			padding-inline ${token('motion.duration.medium')} ${token('motion.easing.in.practical')} ${token('motion.duration.xxshort')}
		`,
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	buttonExpanded: {
		top: token('space.negative.025'),
		left: token('space.negative.025'),
		height: token('space.400'),
		// Offset the added padding so the leading avatar stays in place as the bubble grows.
		paddingInlineStart: token('space.025'),
		paddingInlineEnd: token('space.100'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		'&:active': {
			backgroundColor: token('elevation.rovo.surface.overlay.pressed'),
			transition: token('motion.button.pressed'),
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
			},
		},
	},
	buttonEnd: {
		left: 'auto',
		right: 0,
		flexDirection: 'row-reverse',
		borderBottomLeftRadius: token('radius.xxlarge'),
		borderBottomRightRadius: token('radius.small'),
	},
	buttonExpandedEnd: {
		right: token('space.negative.025'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.025'),
	},
	avatarStackRow: {
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		minWidth: AVATAR_SIZE,
		isolation: 'isolate',
	},
	avatarStackRowEnd: {
		flexDirection: 'row-reverse',
	},
	avatar: {
		display: 'grid',
		alignItems: 'center',
		justifyItems: 'center',
		// Keep a full-size content column as the slot expands from underneath the previous avatar.
		gridTemplateColumns: `${AVATAR_SIZE}px`,
		justifyContent: 'end',
		flexShrink: 0,
		position: 'relative',
		height: AVATAR_SIZE,
		width: AVATAR_SIZE,
		transitionProperty: 'width, opacity',
		transitionDuration: token('motion.duration.medium'),
		transitionTimingFunction: token('motion.easing.in.practical'),
		transitionDelay: token('motion.duration.xxshort'),
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	avatarEnd: {
		justifyContent: 'start',
	},
	avatarCollapsed: { width: 0, opacity: 0 },
	avatarExpanded: {
		width: EXPANDED_AVATAR_SLOT_WIDTH,
		opacity: 1,
		transitionTimingFunction: token('motion.easing.out.practical'),
	},
	avatarExpandedEndLast: {
		transitionDuration: token('motion.duration.short'),
		transitionDelay: token('motion.duration.xshort'),
	},
	previewText: {
		color: token('color.text'),
		font: token('font.body.small'),
		minWidth: 0,
		maxWidth: 0,
		opacity: 0,
		overflow: 'hidden',
		textAlign: 'start',
		whiteSpace: 'nowrap',
		transitionProperty: 'max-width, margin-inline, padding-inline, opacity',
		transitionDuration: `${token('motion.duration.medium')}, ${token('motion.duration.medium')}, ${token('motion.duration.medium')}, ${token('motion.duration.xshort')}`,
		transitionDelay: token('motion.duration.xxshort'),
		transitionTimingFunction: token('motion.easing.in.practical'),
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	previewTextExpanded: {
		maxWidth: MAX_PREVIEW_WIDTH,
		opacity: 1,
		transitionTimingFunction: token('motion.easing.out.practical'),
		marginInlineStart: token('space.025'),
		paddingInlineEnd: token('space.075'),
	},
	previewTextExpandedEnd: {
		marginInlineStart: 0,
		paddingInlineEnd: 0,
		marginInlineEnd: token('space.025'),
		paddingInlineStart: token('space.075'),
	},
	previewTextContent: {
		display: 'block',
		width: 'max-content',
		maxWidth: MAX_PREVIEW_WIDTH,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

/**
 * A profile in the avatar stack. Only agents appear here; human authors are excluded.
 * A slot with no profile renders the generic Rovo hex logo.
 */
export type SuggestionAvatarProfile = { profile?: SuggestionProfile };

export type SuggestionAvatarIndicatorProps = {
	label: string;
	onBlur?: () => void;
	onClick?: () => void;
	onFocus?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	/** Unique agent profiles behind the current suggestion(s) — human authors are not shown. */
	profiles: SuggestionAvatarProfile[];
};

/** An agent avatar stack that expands to show the suggestion label on hover or focus. */
export function SuggestionAvatarIndicator({
	label,
	onBlur,
	onClick,
	onFocus,
	onMouseEnter,
	onMouseLeave,
	profiles,
}: SuggestionAvatarIndicatorProps): JSX.Element {
	const [isExpanded, setIsExpanded] = useState(false);
	const indicatorRef = useRef<HTMLSpanElement | null>(null);
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const previewTextRef = useRef<HTMLSpanElement | null>(null);
	const stackProfiles: SuggestionAvatarProfile[] =
		profiles.length > 0 ? profiles.slice(0, MAX_VISIBLE_AVATARS) : [{}];
	const stackWidth = AVATAR_SIZE + EXPANDED_AVATAR_SLOT_WIDTH * (stackProfiles.length - 1);
	const [horizontalAlign, setHorizontalAlign] = useState<'start' | 'end'>('start');
	const isEndAligned = horizontalAlign === 'end';

	useEffect(() => {
		scrollParentRef.current = findOverflowScrollParent(indicatorRef.current) || null;
	}, []);

	const updateAlignment = () => {
		const indicator = indicatorRef.current;
		const previewText = previewTextRef.current;
		if (!indicator || !previewText) {
			return;
		}
		const editorContainer = scrollParentRef.current;
		if (!editorContainer) {
			return;
		}

		// The button is collapsed, but the inner text already has its expanded width.
		const expandedWidth =
			stackWidth + EXPANDED_INLINE_SPACING + previewText.getBoundingClientRect().width;
		const rightEdge = editorContainer.getBoundingClientRect().right;
		setHorizontalAlign(
			indicator.getBoundingClientRect().left - EXPANDED_EDGE_OFFSET + expandedWidth >
				rightEdge - RIGHT_EDGE_CLEARANCE
				? 'end'
				: 'start',
		);
	};

	const expand = (onExpand?: () => void) => {
		if (!isExpanded) {
			updateAlignment();
		}
		setIsExpanded(true);
		onExpand?.();
	};

	const collapse = (onCollapse?: () => void) => {
		setIsExpanded(false);
		// Reduced motion skips the transitionend event that normally resets alignment.
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
			setHorizontalAlign('start');
		}
		onCollapse?.();
	};

	return (
		<span
			ref={indicatorRef}
			css={styles.indicatorBase}
			onMouseEnter={() => expand(onMouseEnter)}
			onMouseLeave={() => collapse(onMouseLeave)}
			onFocus={() => expand(onFocus)}
			onBlur={() => collapse(onBlur)}
		>
			<button
				type="button"
				data-testid="suggestion-avatar-indicator"
				css={[
					styles.button,
					isExpanded && styles.buttonExpanded,
					isEndAligned && styles.buttonEnd,
					isExpanded && isEndAligned && styles.buttonExpandedEnd,
				]}
				onClick={onClick}
				aria-label={label}
			>
				<span css={[styles.avatarStackRow, isEndAligned && styles.avatarStackRowEnd]}>
					{stackProfiles.map((avatarProfile, index) => (
						<span
							key={avatarProfile.profile?.accountId ?? index}
							data-testid="suggestion-avatar"
							css={[
								styles.avatar,
								isEndAligned && styles.avatarEnd,
								index > 0 && !isExpanded && styles.avatarCollapsed,
								index > 0 && isExpanded && styles.avatarExpanded,
								index === 2 && isExpanded && isEndAligned && styles.avatarExpandedEndLast,
							]}
							style={{
								// Earlier avatars stack in front of later ones.
								zIndex: stackProfiles.length - index,
							}}
						>
							<SuggestionAvatar profile={avatarProfile.profile} size="UNSAFE_xsmall" hasBorder />
						</span>
					))}
				</span>
				<span
					css={[
						styles.previewText,
						isExpanded && styles.previewTextExpanded,
						isExpanded && isEndAligned && styles.previewTextExpandedEnd,
					]}
					// only switch back the alignment after the transition is over
					onTransitionEnd={(event) => {
						if (!isExpanded && event.propertyName === 'max-width') {
							setHorizontalAlign('start');
						}
					}}
					// this is already announced as the button aria-label
					aria-hidden="true"
				>
					{/* Truncate at the expanded width; the outer span clips during the transition. */}
					<span ref={previewTextRef} css={styles.previewTextContent}>
						{label}
					</span>
				</span>
			</button>
		</span>
	);
}
